import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/contexts/AuthContext";
import {
  Plug, MessageSquare, Wallet, Check, ArrowLeft, ArrowRight,
  MessageCircle, Facebook, Instagram, Send, Rocket, Sparkles,
} from "lucide-react";

export default function GetStarted() {
  const { lang } = useLang();
  const ar = lang === "ar";
  const Arrow = ar ? ArrowLeft : ArrowRight;
  const [channels, setChannels] = useState([]);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    api.get("/me/channels").then(({ data }) => setChannels(data.channels || [])).catch(() => {});
    api.get("/me/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  }, []);

  const hasChannel = channels.some((c) => (c.status || "").toLowerCase() === "connected");
  const hasBalance = parseFloat(wallet?.wallet?.balance_omr ?? 0) > 0;

  const openInbox = async () => {
    try {
      const { data } = await api.post("/me/chatwoot/sso");
      if (data?.sso_url) return window.open(data.sso_url, "_blank", "noopener,noreferrer");
    } catch (_) {}
    window.open("https://letsm.io/app", "_blank", "noopener,noreferrer");
  };

  const steps = [
    {
      id: "channels",
      done: hasChannel,
      icon: Plug,
      title: ar ? "اربط قنوات التواصل" : "Connect your channels",
      desc: ar
        ? "اربط واتساب أعمال، فيسبوك ماسنجر، انستقرام أو تيليجرام بضغطة زر — بدون أي خطوات تقنية."
        : "Connect WhatsApp Business, Facebook Messenger, Instagram or Telegram in one click — no technical steps.",
      chips: [
        { icon: MessageCircle, label: "WhatsApp", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
        { icon: Facebook, label: "Messenger", cls: "bg-blue-50 text-blue-800 border-blue-200" },
        { icon: Instagram, label: "Instagram", cls: "bg-pink-50 text-pink-800 border-pink-200" },
        { icon: Send, label: "Telegram", cls: "bg-sky-50 text-sky-800 border-sky-200" },
      ],
      cta: { to: "/dashboard/channels", label: ar ? "اربط قناتك الأولى" : "Connect your first channel", testId: "get-started-channels-btn" },
    },
    {
      id: "inbox",
      done: false,
      icon: MessageSquare,
      title: ar ? "افتح صندوق الرسائل الموحّد" : "Open your unified inbox",
      desc: ar
        ? "كل محادثات عملائك من جميع القنوات في شاشة واحدة. رُد، وزّع المحادثات على فريقك، وتابع كل شيء من مكان واحد."
        : "All customer conversations from every channel in one screen. Reply, assign to your team, and track everything in one place.",
      cta: { onClick: openInbox, label: ar ? "افتح صندوق الرسائل" : "Open Inbox", testId: "get-started-inbox-btn" },
    },
    {
      id: "wallet",
      done: hasBalance,
      icon: Wallet,
      title: ar ? "فعّل باقتك واشحن محفظتك" : "Activate your plan & top up",
      desc: ar
        ? "اختر الباقة المناسبة لحجم أعمالك واشحن محفظتك عبر ثواني (Thawani) لتفعيل الإرسال بدون انقطاع."
        : "Choose the plan that fits your business and top up your wallet via Thawani to keep messaging without interruption.",
      cta: { to: "/dashboard/billing", label: ar ? "عرض الباقات" : "View plans", testId: "get-started-billing-btn" },
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="space-y-8" data-testid="get-started-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-800 to-emerald-950 text-white p-8 lg:p-10">
        <div className="absolute -top-10 -end-10 w-48 h-48 rounded-full bg-amber-400/10 blur-2xl"></div>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-amber-400/20 flex items-center justify-center flex-shrink-0">
            <Rocket size={24} className="text-amber-400" />
          </div>
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-extrabold mb-2" data-testid="get-started-title">
              {ar ? "ابدأ خلال دقائق 🚀" : "Get started in minutes 🚀"}
            </h1>
            <p className="text-emerald-100 text-sm lg:text-base max-w-2xl leading-relaxed">
              {ar
                ? "ثلاث خطوات بسيطة تفصلك عن استقبال رسائل عملائك من واتساب وفيسبوك وانستقرام في صندوق واحد."
                : "Three simple steps separate you from receiving customer messages from WhatsApp, Facebook and Instagram in one inbox."}
            </p>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-2 w-40 rounded-full bg-white/15 overflow-hidden">
                <div
                  className="h-full bg-amber-400 rounded-full transition-all duration-500"
                  style={{ width: `${(doneCount / steps.length) * 100}%` }}
                ></div>
              </div>
              <span className="text-xs font-semibold text-amber-300" data-testid="get-started-progress">
                {doneCount}/{steps.length} {ar ? "مكتملة" : "done"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((s, i) => (
          <Card
            key={s.id}
            data-testid={`get-started-step-${s.id}`}
            className={`border rounded-2xl transition-shadow hover:shadow-md ${s.done ? "border-emerald-300 bg-emerald-50/40" : "border-stone-200 bg-white"}`}
          >
            <CardContent className="p-6 flex flex-col md:flex-row md:items-center gap-5">
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center font-heading font-bold ${s.done ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-500"}`}>
                  {s.done ? <Check size={20} strokeWidth={3} /> : i + 1}
                </div>
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${s.done ? "bg-emerald-100 text-emerald-700" : "bg-amber-50 text-amber-600"}`}>
                  <s.icon size={20} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-bold text-stone-900">{s.title}</h3>
                  {s.done && (
                    <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 text-[10px]">
                      {ar ? "تم ✓" : "Done ✓"}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-stone-500 mt-1 leading-relaxed">{s.desc}</p>
                {s.chips && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {s.chips.map((c) => (
                      <span key={c.label} className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-2.5 py-1 ${c.cls}`}>
                        <c.icon size={12} /> {c.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                {s.cta.to ? (
                  <Button asChild data-testid={s.cta.testId} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl font-semibold">
                    <Link to={s.cta.to}>
                      {s.cta.label} <Arrow size={16} className="ms-1.5" />
                    </Link>
                  </Button>
                ) : (
                  <Button onClick={s.cta.onClick} data-testid={s.cta.testId} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl font-semibold">
                    {s.cta.label} <Arrow size={16} className="ms-1.5" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Help footer */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="get-started-help">
        <Sparkles size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-stone-700 leading-relaxed">
          {ar ? (
            <>واجهت صعوبة في أي خطوة؟ راسلنا على <a href="mailto:hello@socialhub.om" className="font-semibold text-emerald-800 underline underline-offset-2">hello@socialhub.om</a> وسنساعدك في الربط خلال أقل من 24 ساعة.</>
          ) : (
            <>Stuck on any step? Email us at <a href="mailto:hello@socialhub.om" className="font-semibold text-emerald-800 underline underline-offset-2">hello@socialhub.om</a> and we'll help you connect within 24 hours.</>
          )}
        </p>
      </div>
    </div>
  );
}
